import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { DossierPaiementMode } from '@/hooks/queries/usePaiementsFormateurs';
import {
  generateFactureAlgerie,
  type FactureAlgerieInput,
  type EntrepriseEmettrice,
  type ClientB2B,
  type PdfOptions,
} from '@/lib/pdf-facture-algerie';

// Sprint 6.9.3 — Consolidé recouvrement entreprises (créances B2B)
// Table créée par migration 103 (recouvrement_entreprises).

export type RecouvrementStatut = 'a_encaisser' | 'partiel' | 'regle';

export interface FactureRecouvrementRow {
  id: string;
  entreprise_id: string;
  activite_id: string | null;
  tier_id: string | null;
  entreprise_cliente_nom: string;
  annee: number;
  reference_facture: string;
  formation_id: string | null;
  formation_titre: string | null;
  date_formation: string | null;
  lieu: string | null;
  participants: number;
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  montant_encaisse: number;
  statut: RecouvrementStatut;
  date_reglement: string | null;
  mode_reglement: DossierPaiementMode | null;
  reference_virement: string | null;
  commercial_id: string | null;
  observation: string | null;
  operation_id: string | null;
  facture_pdf_url: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface RecouvrementFilter {
  entrepriseId: string | null;
  annee?: number | null;
  statut?: RecouvrementStatut | null;
  tierId?: string | null;
  commercialId?: string | null;
  search?: string;
}

export function useFactureRecouvrement(id: string | null) {
  return useQuery({
    queryKey: ['recouvrement_facture', id],
    enabled: !!id,
    queryFn: async (): Promise<FactureRecouvrementRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('recouvrement_entreprises')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as FactureRecouvrementRow | null) ?? null;
    },
  });
}

export function useRecouvrementEntreprises(filter: RecouvrementFilter) {
  return useQuery({
    queryKey: ['recouvrement_entreprises', filter],
    enabled: !!filter.entrepriseId,
    queryFn: async (): Promise<FactureRecouvrementRow[]> => {
      let q = supabase
        .from('recouvrement_entreprises')
        .select('*')
        .order('annee', { ascending: false })
        .order('date_formation', { ascending: false, nullsFirst: false })
        .limit(2000);
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.annee) q = q.eq('annee', filter.annee);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.tierId) q = q.eq('tier_id', filter.tierId);
      if (filter.commercialId) q = q.eq('commercial_id', filter.commercialId);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(
          `entreprise_cliente_nom.ilike.%${s}%,reference_facture.ilike.%${s}%,formation_titre.ilike.%${s}%,observation.ilike.%${s}%`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as FactureRecouvrementRow[];
    },
  });
}

// =====================================================================
// Helpers parsing
// =====================================================================

const STATUT_FROM_EXCEL: Record<string, RecouvrementStatut> = {
  regle: 'regle',
  réglé: 'regle',
  reglee: 'regle',
  réglée: 'regle',
  paye: 'regle',
  payé: 'regle',
  encaisse: 'regle',
  encaissé: 'regle',
  non_regle: 'a_encaisser',
  'non regle': 'a_encaisser',
  'non réglé': 'a_encaisser',
  'non réglée': 'a_encaisser',
  a_encaisser: 'a_encaisser',
  partiel: 'partiel',
  partielle: 'partiel',
};

const MODE_FROM_EXCEL: Record<string, DossierPaiementMode> = {
  virement: 'virement',
  cheque: 'cheque',
  chèque: 'cheque',
  especes: 'especes',
  espèces: 'especes',
  cash: 'especes',
  ccp: 'ccp',
  carte: 'carte',
};

function normalizeKey(s: string | null | undefined): string {
  return (s ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseStatut(raw: string | null): RecouvrementStatut {
  const k = normalizeKey(raw);
  return STATUT_FROM_EXCEL[k] ?? 'a_encaisser';
}

function parseMode(raw: string | null): DossierPaiementMode | null {
  const k = normalizeKey(raw);
  return MODE_FROM_EXCEL[k] ?? null;
}

function parseDate(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.toString().trim();
  if (!s) return null;
  const m1 = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m1) return `${m1[1]}-${m1[2]}-${m1[3]}`;
  const m2 = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/.exec(s);
  if (m2) {
    const [, d, mo, yRaw] = m2;
    const y = (yRaw ?? '').length === 2 ? `20${yRaw}` : (yRaw ?? '');
    return `${y}-${(mo ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

function parseNumber(raw: string | number | null): number {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') return raw;
  const cleaned = raw.toString().replace(/[^\d-]/g, '');
  if (cleaned === '' || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

function computeStatut(ttc: number, encaisse: number): RecouvrementStatut {
  if (encaisse <= 0) return 'a_encaisser';
  if (encaisse >= ttc) return 'regle';
  return 'partiel';
}

// =====================================================================
// IMPORT — cascade auto entreprises_clientes + formations + factures
// =====================================================================

export interface ImportFactureRow {
  annee: string | number | null;
  reference_facture: string | null;
  formation: string | null;
  date_formation: string | null;
  lieu: string | null;
  entreprise: string | null;
  participants: string | number | null;
  montant_ht: string | number | null;
  montant_tva: string | number | null;
  montant_ttc: string | number | null;
  statut: string | null;
  date_reglement: string | null;
  mode_reglement: string | null;
  reference_virement: string | null;
  commerciale: string | null;
  observation: string | null;
}

export interface ImportRecouvrementInput {
  entrepriseId: string;
  activiteId: string | null;
  rows: ImportFactureRow[];
}

export interface ImportRecouvrementResult {
  facturesCrees: number;
  clientsCrees: number;
  formationsCreees: number;
  commerciauxNonTrouves: string[];
  errors: string[];
}

export function useImportRecouvrement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportRecouvrementInput): Promise<ImportRecouvrementResult> => {
      const result: ImportRecouvrementResult = {
        facturesCrees: 0,
        clientsCrees: 0,
        formationsCreees: 0,
        commerciauxNonTrouves: [],
        errors: [],
      };

      const { data: existingTiers = [] } = await supabase
        .from('tiers')
        .select('id, raison_sociale')
        .eq('entreprise_id', input.entrepriseId)
        .eq('type', 'client_b2b');
      const tierByKey = new Map<string, string>();
      for (const t of existingTiers as { id: string; raison_sociale: string }[]) {
        tierByKey.set(normalizeKey(t.raison_sociale), t.id);
      }

      const { data: existingFormations = [] } = await supabase
        .from('formations')
        .select('id, titre')
        .eq('entreprise_id', input.entrepriseId);
      const formationByKey = new Map<string, string>();
      for (const f of existingFormations as { id: string; titre: string }[]) {
        formationByKey.set(normalizeKey(f.titre), f.id);
      }

      // Charge tous les utilisateurs + métadonnées pour résoudre les commerciaux par prénom.
      const { data: usersMeta = [] } = await supabase
        .from('users_metadata')
        .select('user_id, full_name');
      const userByPrenom = new Map<string, string>();
      for (const u of usersMeta as { user_id: string; full_name: string | null }[]) {
        if (!u.full_name) continue;
        const prenom = u.full_name.trim().split(/\s+/)[0] ?? '';
        if (prenom) userByPrenom.set(normalizeKey(prenom), u.user_id);
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      for (let i = 0; i < input.rows.length; i++) {
        const row = input.rows[i];
        if (!row) continue;
        const lineNo = i + 2;
        try {
          const entrepriseNom = (row.entreprise ?? '').toString().trim();
          const refFacture = (row.reference_facture ?? '').toString().trim();
          if (!entrepriseNom) {
            result.errors.push(`L${lineNo}: entreprise cliente manquante`);
            continue;
          }
          if (!refFacture) {
            result.errors.push(`L${lineNo}: référence facture manquante`);
            continue;
          }
          const annee = parseNumber(row.annee) || new Date().getFullYear();
          const ht = parseNumber(row.montant_ht);
          const tva = parseNumber(row.montant_tva);
          const ttc = parseNumber(row.montant_ttc) || ht + tva;
          const participants = parseNumber(row.participants);

          // 1. Tier client B2B — find or create
          const tierKey = normalizeKey(entrepriseNom);
          let tierId = tierByKey.get(tierKey) ?? null;
          if (!tierId) {
            const { data: created, error: errTier } = await supabase
              .from('tiers')
              .insert({
                entreprise_id: input.entrepriseId,
                type: 'client_b2b',
                raison_sociale: entrepriseNom,
                activite_principale_id: input.activiteId,
              } as never)
              .select('id')
              .single();
            if (errTier) {
              result.errors.push(`L${lineNo}: client B2B KO — ${errTier.message}`);
              continue;
            }
            tierId = (created as { id: string } | null)?.id ?? null;
            if (tierId) {
              tierByKey.set(tierKey, tierId);
              result.clientsCrees++;
            }
          }

          // 2. Formation — find or create (optionnel)
          let formationId: string | null = null;
          const formationTitre = (row.formation ?? '').toString().trim();
          if (formationTitre) {
            const formKey = normalizeKey(formationTitre);
            formationId = formationByKey.get(formKey) ?? null;
            if (!formationId) {
              const code = `IMP-${Math.floor(Math.random() * 9000 + 1000)}`;
              const { data: createdF, error: errF } = await supabase
                .from('formations')
                .insert({
                  entreprise_id: input.entrepriseId,
                  code,
                  titre: formationTitre,
                } as never)
                .select('id')
                .single();
              if (errF) {
                result.errors.push(`L${lineNo}: formation KO — ${errF.message}`);
              } else {
                formationId = (createdF as { id: string } | null)?.id ?? null;
                if (formationId) {
                  formationByKey.set(formKey, formationId);
                  result.formationsCreees++;
                }
              }
            }
          }

          // 3. Commercial — lookup par prénom (best-effort)
          let commercialId: string | null = null;
          const commercialeRaw = (row.commerciale ?? '').toString().trim();
          if (commercialeRaw) {
            commercialId = userByPrenom.get(normalizeKey(commercialeRaw)) ?? null;
            if (!commercialId && !result.commerciauxNonTrouves.includes(commercialeRaw)) {
              result.commerciauxNonTrouves.push(commercialeRaw);
            }
          }

          // 4. Statut : priorité Excel, sinon dérivé
          const statutExcel = parseStatut(row.statut);
          const encaisseInit = statutExcel === 'regle' ? ttc : statutExcel === 'partiel' ? 0 : 0;
          const statut =
            row.statut && row.statut.toString().trim() !== ''
              ? statutExcel
              : computeStatut(ttc, encaisseInit);

          // 5. INSERT facture
          const { error: errIns } = await supabase.from('recouvrement_entreprises').insert({
            entreprise_id: input.entrepriseId,
            activite_id: input.activiteId,
            tier_id: tierId,
            entreprise_cliente_nom: entrepriseNom,
            annee,
            reference_facture: refFacture,
            formation_id: formationId,
            formation_titre: formationTitre || null,
            date_formation: parseDate(row.date_formation),
            lieu: row.lieu ?? null,
            participants,
            montant_ht: ht,
            montant_tva: tva,
            montant_ttc: ttc,
            montant_encaisse: encaisseInit,
            statut,
            date_reglement: parseDate(row.date_reglement),
            mode_reglement: parseMode(row.mode_reglement),
            reference_virement: row.reference_virement ?? null,
            commercial_id: commercialId,
            observation: row.observation ?? null,
            created_by: createdBy,
          } as never);
          if (errIns) {
            result.errors.push(`L${lineNo}: insert facture KO — ${errIns.message}`);
            continue;
          }
          result.facturesCrees++;
        } catch (e) {
          result.errors.push(`L${lineNo}: ${(e as Error).message}`);
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recouvrement_entreprises'] });
      void qc.invalidateQueries({ queryKey: ['tiers'] });
      void qc.invalidateQueries({ queryKey: ['formations'] });
    },
  });
}

// =====================================================================
// CRUD manuel
// =====================================================================

export interface CreateFactureInput {
  entreprise_id: string;
  activite_id: string | null;
  tier_id: string | null;
  entreprise_cliente_nom: string;
  annee: number;
  reference_facture: string;
  formation_id: string | null;
  formation_titre: string | null;
  date_formation: string | null;
  lieu: string | null;
  participants: number;
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  montant_encaisse: number;
  statut: RecouvrementStatut;
  date_reglement: string | null;
  mode_reglement: DossierPaiementMode | null;
  reference_virement: string | null;
  commercial_id: string | null;
  observation: string | null;
}

export function useCreateFacture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFactureInput): Promise<FactureRecouvrementRow> => {
      const { data, error } = await supabase
        .from('recouvrement_entreprises')
        .insert(input as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as FactureRecouvrementRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recouvrement_entreprises'] });
    },
  });
}

export interface UpdateFactureInput {
  id: string;
  annee?: number;
  tier_id?: string | null;
  entreprise_cliente_nom?: string;
  reference_facture?: string;
  formation_id?: string | null;
  formation_titre?: string | null;
  date_formation?: string | null;
  lieu?: string | null;
  participants?: number;
  montant_ht?: number;
  montant_tva?: number;
  montant_ttc?: number;
  montant_encaisse?: number;
  statut?: RecouvrementStatut;
  date_reglement?: string | null;
  mode_reglement?: DossierPaiementMode | null;
  reference_virement?: string | null;
  commercial_id?: string | null;
  observation?: string | null;
  facture_pdf_url?: string | null;
}

export function useUpdateFacture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateFactureInput): Promise<void> => {
      const { id, ...rest } = input;
      const { error } = await supabase
        .from('recouvrement_entreprises')
        .update(rest as never)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recouvrement_entreprises'] });
    },
  });
}

export function useDeleteFacture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (facture: { id: string; operation_id: string | null }): Promise<void> => {
      if (facture.operation_id) {
        const { error: errOp } = await supabase
          .from('operations')
          .delete()
          .eq('id', facture.operation_id);
        if (errOp && !/no rows/i.test(errOp.message)) {
          throw new Error(`Suppression opération Caisse KO : ${errOp.message}`);
        }
      }
      const { error } = await supabase
        .from('recouvrement_entreprises')
        .delete()
        .eq('id', facture.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recouvrement_entreprises'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

// =====================================================================
// ENREGISTRER ENCAISSEMENT (partiel ou full) — cross-ref operation ENTRÉE
// =====================================================================

export interface EnregistrerEncaissementInput {
  facture: FactureRecouvrementRow;
  montantEncaisse: number;
  dateReglement: string;
  modePaiement: DossierPaiementMode;
  reference: string | null;
  caisseId: string | null;
  compteBancaireId: string | null;
}

export function useEnregistrerEncaissement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: EnregistrerEncaissementInput,
    ): Promise<{ operation_id: string; nouveau_statut: RecouvrementStatut }> => {
      const {
        facture,
        montantEncaisse,
        dateReglement,
        modePaiement,
        reference,
        caisseId,
        compteBancaireId,
      } = input;

      if (montantEncaisse <= 0) throw new Error('Montant à encaisser > 0 obligatoire');
      const reste = facture.montant_ttc - facture.montant_encaisse;
      if (montantEncaisse > reste + 0.01) {
        throw new Error(
          `Montant trop élevé : reste à encaisser ${reste.toLocaleString('fr-DZ')} DA`,
        );
      }

      let canal: 'especes' | 'ccp' | 'banque';
      if (modePaiement === 'especes') canal = 'especes';
      else if (modePaiement === 'ccp') canal = 'ccp';
      else canal = 'banque';

      if (canal === 'especes' && !caisseId) {
        throw new Error('Caisse obligatoire pour un encaissement en espèces');
      }
      if (canal !== 'especes' && !compteBancaireId) {
        throw new Error('Compte bancaire obligatoire pour ce mode');
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const reference_op =
        reference ?? `ENC-${facture.annee}-${Math.floor(Math.random() * 9000 + 1000)}`;
      const motif = `Encaissement facture ${facture.reference_facture} — ${facture.entreprise_cliente_nom}`;

      const { data: opCreated, error: errOp } = await supabase
        .from('operations')
        .insert({
          entreprise_id: facture.entreprise_id,
          reference: reference_op,
          date_operation: `${dateReglement}T12:00:00`,
          sens: 'entree',
          canal,
          categorie: 'vente_service',
          caisse_id: canal === 'especes' ? caisseId : null,
          compte_bancaire_id: canal !== 'especes' ? compteBancaireId : null,
          tier_id: facture.tier_id,
          activite_id: facture.activite_id,
          activite_beneficiaire_id: facture.activite_id,
          activite_emettrice_id: facture.activite_id,
          devise_code: 'DZD',
          montant: montantEncaisse,
          taux_change: 1,
          montant_dzd: montantEncaisse,
          motif,
          observation: `Facture ${facture.id.slice(0, 8)} — ${facture.montant_encaisse + montantEncaisse >= facture.montant_ttc ? 'soldée' : 'encaissement partiel'}`,
          statut: 'validee',
          created_by: user?.id ?? null,
        } as never)
        .select('id')
        .single();
      if (errOp) throw new Error(`Opération KO : ${errOp.message}`);
      const operationId = (opCreated as { id: string } | null)?.id;
      if (!operationId) throw new Error('Opération créée sans id');

      const nouveauEncaisse = facture.montant_encaisse + montantEncaisse;
      const nouveauStatut: RecouvrementStatut =
        nouveauEncaisse >= facture.montant_ttc ? 'regle' : 'partiel';

      const { error: errUpd } = await supabase
        .from('recouvrement_entreprises')
        .update({
          montant_encaisse: nouveauEncaisse,
          statut: nouveauStatut,
          mode_reglement: modePaiement,
          reference_virement: reference_op,
          date_reglement: dateReglement,
          operation_id: nouveauStatut === 'regle' ? operationId : facture.operation_id,
        } as never)
        .eq('id', facture.id);
      if (errUpd) throw new Error(`Update facture KO : ${errUpd.message}`);

      return { operation_id: operationId, nouveau_statut: nouveauStatut };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recouvrement_entreprises'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

// =====================================================================
// BULK encaissement (1 modal global)
// =====================================================================

export interface BulkEncaisserInput {
  factures: FactureRecouvrementRow[];
  dateReglement: string;
  modePaiement: DossierPaiementMode;
  caisseId: string | null;
  compteBancaireId: string | null;
}

export interface BulkEncaisserResult {
  success: number;
  errors: { facture_id: string; message: string }[];
}

export function useBulkEncaisserFactures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkEncaisserInput): Promise<BulkEncaisserResult> => {
      const result: BulkEncaisserResult = { success: 0, errors: [] };
      let canal: 'especes' | 'ccp' | 'banque';
      if (input.modePaiement === 'especes') canal = 'especes';
      else if (input.modePaiement === 'ccp') canal = 'ccp';
      else canal = 'banque';
      if (canal === 'especes' && !input.caisseId) {
        throw new Error('Caisse obligatoire pour un encaissement en espèces');
      }
      if (canal !== 'especes' && !input.compteBancaireId) {
        throw new Error('Compte bancaire obligatoire pour ce mode');
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      for (const facture of input.factures) {
        if (facture.statut === 'regle') continue;
        const reste = facture.montant_ttc - facture.montant_encaisse;
        if (reste <= 0) continue;
        try {
          const ref = `ENC-${facture.annee}-${Math.floor(Math.random() * 9000 + 1000)}`;
          const motif = `Encaissement facture ${facture.reference_facture} — ${facture.entreprise_cliente_nom}`;
          const { data: opCreated, error: errOp } = await supabase
            .from('operations')
            .insert({
              entreprise_id: facture.entreprise_id,
              reference: ref,
              date_operation: `${input.dateReglement}T12:00:00`,
              sens: 'entree',
              canal,
              categorie: 'vente_service',
              caisse_id: canal === 'especes' ? input.caisseId : null,
              compte_bancaire_id: canal !== 'especes' ? input.compteBancaireId : null,
              tier_id: facture.tier_id,
              activite_id: facture.activite_id,
              activite_beneficiaire_id: facture.activite_id,
              activite_emettrice_id: facture.activite_id,
              devise_code: 'DZD',
              montant: reste,
              taux_change: 1,
              montant_dzd: reste,
              motif,
              observation: `Bulk encaissement — facture ${facture.id.slice(0, 8)} soldée`,
              statut: 'validee',
              created_by: createdBy,
            } as never)
            .select('id')
            .single();
          if (errOp) {
            result.errors.push({ facture_id: facture.id, message: errOp.message });
            continue;
          }
          const operationId = (opCreated as { id: string } | null)?.id;
          if (!operationId) {
            result.errors.push({ facture_id: facture.id, message: 'Op créée sans id' });
            continue;
          }
          const { error: errUpd } = await supabase
            .from('recouvrement_entreprises')
            .update({
              montant_encaisse: facture.montant_ttc,
              statut: 'regle',
              mode_reglement: input.modePaiement,
              reference_virement: ref,
              date_reglement: input.dateReglement,
              operation_id: operationId,
            } as never)
            .eq('id', facture.id);
          if (errUpd) {
            result.errors.push({ facture_id: facture.id, message: errUpd.message });
            continue;
          }
          result.success++;
        } catch (e) {
          result.errors.push({ facture_id: facture.id, message: (e as Error).message });
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recouvrement_entreprises'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

// =====================================================================
// PDF FACTURE — génération + upload Storage
// =====================================================================

export interface GeneratePdfInput {
  facture: FactureRecouvrementRow;
  dateEmission: string;
  dateEcheance: string;
  type: 'proforma' | 'definitive';
  includeCgv: boolean;
  emettrice: EntrepriseEmettrice;
  client: ClientB2B;
}

export function useGenerateFacturePdf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: GeneratePdfInput,
    ): Promise<{ url: string; signedUrl: string; blob: Blob }> => {
      const factureInput: FactureAlgerieInput = {
        reference_facture: input.facture.reference_facture,
        date_emission: input.dateEmission,
        date_echeance: input.dateEcheance,
        type: input.type,
        designation_principale:
          input.facture.formation_titre ?? `Prestation ${input.facture.annee}`,
        participants: input.facture.participants,
        date_formation: input.facture.date_formation,
        lieu: input.facture.lieu,
        montant_ht: input.facture.montant_ht,
        montant_tva: input.facture.montant_tva,
        montant_ttc: input.facture.montant_ttc,
        observation: input.facture.observation,
      };
      const options: PdfOptions = { includeCgv: input.includeCgv };
      const blob = await generateFactureAlgerie(
        factureInput,
        input.emettrice,
        input.client,
        options,
      );

      // Upload Storage bucket "factures"
      const safeName = input.facture.reference_facture.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${input.facture.annee}/${safeName}-${Date.now()}.pdf`;
      const { error: errUp } = await supabase.storage.from('factures').upload(path, blob, {
        contentType: 'application/pdf',
        upsert: true,
      });
      if (errUp) throw new Error(`Upload PDF KO : ${errUp.message}`);

      // Update facture avec URL stockage
      const { error: errUpd } = await supabase
        .from('recouvrement_entreprises')
        .update({ facture_pdf_url: path } as never)
        .eq('id', input.facture.id);
      if (errUpd) throw new Error(`Update PDF URL KO : ${errUpd.message}`);

      // Génère un signed URL pour download immédiat (10 min)
      const { data: signed, error: errSign } = await supabase.storage
        .from('factures')
        .createSignedUrl(path, 600);
      if (errSign || !signed) throw new Error(`Signed URL KO : ${errSign?.message ?? '?'}`);

      return { url: path, signedUrl: signed.signedUrl, blob };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recouvrement_entreprises'] });
    },
  });
}
